import React from 'react';

import { Glossary, BoardGL } from 'common/utils/glossary';
import {
  Button,
  Message,
  Checkbox,
  Separator,
  Title,
  LinkText,
  Spinner
} from 'common/components';
import { SubButtonType } from 'common/components/button';
import { TerminalDeleteDialog } from 'pages/board';

interface TerminalBoardProps {
  /** TerminalBoardの表示有無 */
  isDisplay: boolean;
  /** グロッサリ */
  glossary: Glossary;
  /** デバイス利用上限 */
  planLimit: number;
  /** デバイス利用量 */
  planUsage: number;
  /** 取得中か否か */
  isTerminalListProcessing: boolean;
  /** 取得が失敗か否か */
  isTerminalListProcessingFail: boolean;
  /** 取得が成功したか否か */
  isTerminalListProcessingUntreated: boolean;
  /**
   * 端末リストマップ
   * {デバイス利用状況のインデックス(situation_index): 端末情報のリスト}
   **/
  terminalMap: {
    [key: string]: {
      id: string;
      name: string;
      isDelete: boolean;
    }[];
  };
  /** "クローズボタン"の押下処理 */
  onClickClose: () => void;
  /** "削除ボタン"の押下処理 */
  onClickDelete: () => void;
  /** 削除対象フラグ更新処理 */
  updateDelete: (terminalId: string, isDelete: boolean) => void;
}

/**
 * 端末管理画面のPresentation Component
 */
export const TerminalBoard: React.FC<TerminalBoardProps> = ({
  isDisplay,
  glossary,
  planLimit,
  planUsage,
  isTerminalListProcessing,
  isTerminalListProcessingFail,
  isTerminalListProcessingUntreated,
  terminalMap,
  onClickClose,
  onClickDelete,
  updateDelete
}) => {
  /** チェックボックス押下時の処理 */
  const onClickCheckBox = (event: any) => {
    // eventから更新に必要なインデックス情報を取得
    const index = event.target.id;
    const terminal_id = event.target.value;
    /** 対象データの削除対象フラグを更新 */
    terminalMap[index].map(function (terminal) {
      if (terminal.id == terminal_id) {
        updateDelete(terminal_id, !terminal.isDelete);
      }
    });
  };

  /** チェックボックスラベル押下時の処理 */
  function createOnClickCheckBoxLabel(key: string, id: string) {
    const onClickCheckBoxLabel = () => {
      /** 対象データの削除対象フラグを更新 */
      terminalMap[key].map(function (terminal) {
        if (terminal.id == id) {
          updateDelete(id, !terminal.isDelete);
        }
      });
    };
    return onClickCheckBoxLabel;
  }

  // リスト部に表示するComponentをメモライズ
  const listComponent = React.useMemo(() => {
    const components: JSX.Element[] = [];
    // デバイス利用状況のインデックスを昇順にソート
    const keyList = Object.keys(terminalMap).sort();
    for (const key of keyList) {
      // デバイス利用状況インデックスを追加
      components.push(
        <Message
          type="message"
          data-test={`terminalBoard-situation-index-${key}`}
        >
          {'#{0}'.replace('{0}', key)}
        </Message>
      );
      // 端末情報を追加
      const tmpComponents = terminalMap[key].map(function (terminal) {
        return (
          <>
            <div className="terminalBoard-deviceInfo">
              <Checkbox
                id={key}
                value={terminal.id}
                checked={terminal.isDelete}
                onChange={onClickCheckBox}
                data-test={`terminalBoard-checkbox-${key}-${terminal.id}`}
              />
              <div>
                <LinkText
                  type="checkBoxLabel"
                  className="checkbox-label"
                  onClick={createOnClickCheckBoxLabel(key, terminal.id)}
                  data-test={`terminalBoard-linktext-1-${key}-${terminal.id}`}
                >
                  {glossary
                    .glx(BoardGL.TerminalBoardDeviceName)
                    .replace('{0}', terminal.name || '')}
                </LinkText>
              </div>
            </div>
          </>
        );
      });
      // 配列をマージ
      components.push(...tmpComponents);
      // セパレータを追加
      components.push(
        <Separator
          type="separator"
          data-test={`terminalBoard-separator-${key}`}
        />
      );
    }
    // 最後のセパレータは不要なため削除
    components.pop();
    return components;
  }, [terminalMap]);

  /** チェック状態によって削除ボタンのサブタイプを制御 */
  let subButtonType: SubButtonType = 'invalid';
  for (const situationIndex of Object.keys(terminalMap)) {
    for (const terminal of terminalMap[situationIndex]) {
      if (terminal.isDelete == true) {
        // if (checkedState[situationIndex][terminal.id] == true) {
        // 一つでもチェックされていれば'highPriority'に変更
        subButtonType = 'highPriority';
        break;
      }
    }
    // 'highPriority'に変更済みならば、ループを抜ける
    if (subButtonType == 'highPriority') {
      break;
    }
  }

  const _component = (
    <>
      <TerminalDeleteDialog />
      <div className="terminalBoard" data-test="terminalBoard">
        <Button
          type="toolButton"
          subType="close"
          data-test="terminalBoard-toolButton"
          onClick={onClickClose}
        />
        <Title type="title" data-test="terminalBoard-title">
          {glossary.glx(BoardGL.TerminalBoardTitle)}
          <br />({planUsage}/{planLimit})
        </Title>
        <Title
          type="listMessage"
          data-test="terminalBoard-annotation-listMessage"
        >
          {glossary.glx(BoardGL.TerminalBoardBuyerMS)}
        </Title>
        <Separator type="separator" data-test="terminalBoardSeparator1" />
        <div
          className="terminalBoard-DisplayArea"
          data-test="terminalBoard-DisplayArea"
        >
          {listComponent}
          {isTerminalListProcessing ? <Spinner /> : null}
          {isTerminalListProcessingFail && (
            <Message
              type="message"
              className="errorMessage"
              data-test="terminalBoard-message_error"
            >
              {glossary.glx(BoardGL.TerminalBoardCouldNotGet)}
            </Message>
          )}
          {isTerminalListProcessingUntreated &&
            Object.keys(terminalMap).length === 0 && (
              <Message
                type="message"
                className="errorMessage"
                data-test="terminalBoard-message_empty"
              >
                {glossary.glx(BoardGL.TerminalBoardNotRegister)}
              </Message>
            )}
        </div>
        <Separator type="separator" data-test="terminalBoardSeparator2" />
        <div className="footer">
          <Button
            type="deleteButton"
            subType={subButtonType}
            data-test="terminalBoard-deleteButton"
            onClick={onClickDelete}
          >
            {glossary.glx(BoardGL.TerminalBoardDelete)}
          </Button>
        </div>
      </div>
    </>
  );
  return isDisplay ? _component : null;
};
